import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import CreateOrEditInvoiceItemModal from '@modals/CreateOrEditInvoiceItemModal'
import ManageInvoiceBillingDetailsModal from '@modals/ManageInvoiceBillingDetailsModal'
import ManageInvoiceCampaignModal from '@modals/ManageInvoiceCampaignModal'
import ManageInvoiceDebtorModal from '@modals/ManageInvoiceDebtorModal'

import { InvoiceModel } from '@domain-group/realhub-backend-api-types'
import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditInvoiceItemModal: false,
  showManageInvoiceBillingDetailsModal: false,
  showManageInvoiceCampaignModal: false,
  showManageInvoiceDebtorModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditInvoiceItemModal: {
      closeModal: () => setState({ showCreateOrEditInvoiceItemModal: false }),
      createInvoiceItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      reprocessInvoiceItemToZuora: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateInvoiceItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageInvoiceBillingDetailsModal: {
      closeModal: () => setState({ showManageInvoiceBillingDetailsModal: false }),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageInvoiceDebtorModal: {
      closeModal: () => setState({ showManageInvoiceDebtorModal: false }),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageInvoiceCampaignModal: {
      closeModal: () => setState({ showManageInvoiceCampaignModal: false }),
      updateInvoice: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Details = () => {
  const { invoiceId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)

  const {
    showCreateOrEditInvoiceItemModal,
    showManageInvoiceBillingDetailsModal,
    showManageInvoiceCampaignModal,
    showManageInvoiceDebtorModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditInvoiceItemModal: (payload: { invoiceItem?: InvoiceModel }) => {
        setModalData('CreateOrEditInvoiceItemModal', payload)
        setState({ showCreateOrEditInvoiceItemModal: true })
      },
      showManageInvoiceBillingDetailsModal: (payload: {invoice: InvoiceModel}) => {
        setModalData('ManageInvoiceBillingDetailsModal', payload)
        setState({ showManageInvoiceBillingDetailsModal: true })
      },
      showManageInvoiceDebtorModal: (payload: {invoice: InvoiceModel}) => {
        setModalData('ManageInvoiceDebtorModal', payload)
        setState({ showManageInvoiceDebtorModal: true })
      },
      showManageInvoiceCampaignModal: (payload: {invoice: InvoiceModel}) => {
        setModalData('ManageInvoiceCampaignModal', payload)
        setState({ showManageInvoiceCampaignModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent invoiceId={invoiceId} />

        <CreateOrEditInvoiceItemModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditInvoiceItemModal', setState)}
          showModal={showCreateOrEditInvoiceItemModal}
        />

        <ManageInvoiceBillingDetailsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageInvoiceBillingDetailsModal', setState)}
          showModal={showManageInvoiceBillingDetailsModal}
        />

        <ManageInvoiceDebtorModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageInvoiceDebtorModal', setState)}
          showModal={showManageInvoiceDebtorModal}
        />

        <ManageInvoiceCampaignModal
          callbacks={callbacks('ManageInvoiceCampaignModal', setState)}
          showModal={showManageInvoiceCampaignModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
